<template>
    <Table
        v-if="catalog"
        class-name="add-to-catalog-table"
        :value="selected_items"
        :columns="columns"
        :rows="productsWithSelectable"
        :rows-count="products_count"
        :loading="products_loading"
        :filters="products_filters"
        :filters-conf="filtersConf"
        :placeholder-description="placeholderDescription"
        :placeholder-title="placeholderTitle"
        :show-select="true"
        :dense="true"
        :hide-default-footer="products_count <= pageSize"
        :autofocus-search="true"
        selectable-key="selectable"
        item-key="id"
        item-value="id"
        @input="updateSelectedItems"
        @on-row-click="onRowClick"
        @on-filters-change="onFiltersChange"
    >
        <template slot="product" slot-scope="props">
            <div class="product-cell py-2">
                <div class="text-caption grey--text text--darken-1">
                    {{ props.row.upc || "—" }}
                </div>
                <ProductName :product="props.row" />
            </div>
        </template>
        <template slot="catalog" slot-scope="props">
            <AddToCatalogCatalogCell
                :catalog-entries="props.row.catalog_entries || []"
                :product-format-name="productFormatName(props.row)"
            />
        </template>
        <template slot="format" slot-scope="props">
            <AddToCatalogCatalogCell
                v-if="isProductSelected(props.row)"
                :product="props.row"
                :show-catalog-entries="false"
                :show-format-picker="true"
                :format-id="selectedFormat(props.row.id)"
                :format-options-list="formatOptions"
                :formats-loading="formats_loading"
                :product-format-name="productFormatName(props.row)"
                @format-change="onFormatChange(props.row, $event)"
            />
            <span
                v-if="!isProductSelected(props.row)"
                class="text-caption grey--text text--darken-1"
            >
                {{ $translate("selectProductFirst") }}
            </span>
        </template>
        <template slot="unit_amount" slot-scope="props">
            <v-text-field
                v-if="isProductSelected(props.row)"
                :value="selectedUnitAmount(props.row.id)"
                :label="unitAmountLabel"
                type="text"
                inputmode="decimal"
                dense
                outlined
                hide-details
                prefix="€"
                class="unit-amount-input"
                @input="onUnitAmountChange(props.row, $event)"
            />
            <span
                v-else
                class="text-caption grey--text text--darken-1"
            >
                {{ $translate("selectProductFirst") }}
            </span>
        </template>
    </Table>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";

import GlobalMixins from "@/mixins/global";
import AddToCatalogCatalogCell from "./addtocatalog-catalog-cell";

export default {
    mixins: [GlobalMixins],
    components: { AddToCatalogCatalogCell },
    computed: {
        ...mapState("addtocatalog", [
            "catalog",
            "locked_provider",
            "selected_items",
            "formats",
            "formats_loading",
        ]),
        ...mapState("addtocatalog/products", [
            "products",
            "products_count",
            "products_loading",
            "products_filters",
        ]),
        placeholderDescription() {
            return this.$translate("no_product_available_description");
        },
        placeholderTitle() {
            return this.$translate("no_product_available");
        },
        formatOptions() {
            return this.formats || [];
        },
        pageSize() {
            return this.products_filters?.limit || 20;
        },
        unitAmountLabel() {
            return this.catalog?.prices_include_vat
                ? this.$translate("sellPriceInclVat")
                : this.$translate("sellPriceExclVat");
        },
        columns() {
            return [
                {
                    label: this.$translate("product"),
                    field: "product",
                },
                {
                    label: this.$translate("existingEntries"),
                    field: "catalog",
                    sortable: false,
                },
                {
                    label: this.$translate("format"),
                    field: "format",
                    sortable: false,
                },
                {
                    label: this.unitAmountLabel,
                    field: "unit_amount",
                    sortable: false,
                },
            ];
        },
        productsWithSelectable() {
            return this.products.map((product) => ({
                ...product,
                selectable: true,
            }));
        },
        filtersConf() {
            const filters = [
                {
                    preset: "search",
                },
            ];
            if (!this.locked_provider) {
                filters.push({
                    preset: "provider",
                    cols: 6,
                });
            }
            filters.push(
                {
                    field: "exclude_catalog_products",
                    type: "boolean",
                    label: this.$translate("excludeCatalogProducts"),
                    value: this.catalog?.id,
                    cols: 6,
                },
                {
                    field: "with_stock",
                    type: "boolean",
                    label: this.$translate("excludeEmptyStocks"),
                    cols: 6,
                },
            );
            return filters;
        },
    },
    methods: {
        ...mapActions("addtocatalog/products", ["fetchProducts"]),
        ...mapMutations("addtocatalog/products", ["updateProductsFilters"]),
        ...mapMutations("addtocatalog", [
            "toggleItem",
            "selectItem",
            "updateSelectedItems",
            "updateSelectedItemFormat",
            "updateSelectedItemUnitAmount",
        ]),
        isProductSelected(product) {
            return this.selected_items.some((item) => item.id === product.id);
        },
        productFormatName(product) {
            return product?.format_data?.name || null;
        },
        selectedFormat(productId) {
            const item = this.selected_items.find(
                (selected) => selected.id === productId,
            );
            return item?.add_format ?? null;
        },
        selectedUnitAmount(productId) {
            const item = this.selected_items.find(
                (selected) => selected.id === productId,
            );
            return item?.add_unit_amount ?? "";
        },
        onFormatChange(product, formatId) {
            if (!this.isProductSelected(product)) {
                this.selectItem(product);
            }
            this.updateSelectedItemFormat({
                productId: product.id,
                formatId: formatId || null,
            });
        },
        onUnitAmountChange(product, value) {
            this.updateSelectedItemUnitAmount({
                productId: product.id,
                unitAmount: this.$utils.normalizeDecimalString(value) || "",
            });
        },
        onRowClick(item) {
            this.toggleItem(item);
        },
        onFiltersChange(filters) {
            this.updateProductsFilters({
                ...filters,
                offset: 0,
            });
            this.fetchProducts();
        },
    },
    i18n: {
        messages: {
            en: {
                no_product_available: "No product available",
                no_product_available_description: "No product matches the search.",
                product: "Product",
                existingEntries: "Existing entries",
                format: "Format to add",
                sellPriceExclVat: "Sell price (excl. VAT)",
                sellPriceInclVat: "Sell price (incl. VAT)",
                selectProductFirst: "Select the product first",
                excludeCatalogProducts: "Hide products already in catalog",
                excludeEmptyStocks: "Exclude empty stocks",
            },
            fr: {
                no_product_available: "Aucun produit disponible",
                no_product_available_description:
                    "Aucun produit ne correspond à la recherche.",
                product: "Produit",
                existingEntries: "Entrées existantes",
                format: "Format à ajouter",
                sellPriceExclVat: "Prix de vente (HT)",
                sellPriceInclVat: "Prix de vente (TTC)",
                selectProductFirst: "Sélectionnez d'abord le produit",
                excludeCatalogProducts: "Masquer les produits déjà au catalogue",
                excludeEmptyStocks: "Exclure les stocks vides",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.product-cell {
    min-width: 220px;
}

.unit-amount-input {
    min-width: 150px;
    max-width: 180px;
}
</style>
